import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Button } from '../../components/ui/Button';
import { ProgressSpinner } from 'primereact/progressspinner';

import { searchYoutube } from '../../queries/youtube';
import { createLink } from '../../queries/folderMedia';
import { useCustomToast } from '../Contexts/ToastContext';
import { galleryRefetch } from '../../redux/Slices/scrollSlice';

const PAGE_SIZE = 20;

function formatDuration(seconds) {
  const s = Number(seconds) || 0;
  if (s <= 0) return '';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

/**
 * Standalone YouTube search → add to Links library.
 *
 * Spec (popup-push-frontend/.claude/youtube-search-spec.md):
 *   - Backend fetches 60 results; SPA paginates 20 per page locally.
 *   - Search fires ONLY on Enter key or Search button click — NOT on keystroke.
 *   - 24h server-side cache per query.
 *   - Card shows thumbnail + title + duration + channel.
 */
export default function YouTubeSearchDialog({ visible, onHide, onPickedVideo, onAdded }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  // Picker mode = parent wants to receive the chosen video (URL + title)
  // instead of having us save it to the Links library. Used by the Birthday
  // start dialog (one-off track) and the Birthday audio template picker.
  const pickerMode = typeof onPickedVideo === 'function';

  const [query, setQuery] = useState('');
  const [committed, setCommitted] = useState('');
  const [results, setResults] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedVideos, setSelectedVideos] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!visible) {
      reqIdRef.current++;
      setQuery('');
      setCommitted('');
      setResults([]);
      setVisibleCount(PAGE_SIZE);
      setSelectedVideos({});
      setSearchError(null);
    }
  }, [visible]);

  const runSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed || isSearching) return;
    const myReq = ++reqIdRef.current;
    setCommitted(trimmed);
    setIsSearching(true);
    setSearchError(null);
    setResults([]);
    setVisibleCount(PAGE_SIZE);
    try {
      const data = await searchYoutube(trimmed);
      if (myReq !== reqIdRef.current) return;
      setResults(data || []);
    } catch {
      if (myReq !== reqIdRef.current) return;
      setSearchError(t('links_.yt_search_failed'));
      setResults([]);
    } finally {
      if (myReq === reqIdRef.current) setIsSearching(false);
    }
  };

  const clearAll = () => {
    reqIdRef.current++;
    setQuery('');
    setCommitted('');
    setResults([]);
    setVisibleCount(PAGE_SIZE);
    setSelectedVideos({});
    setSearchError(null);
  };

  const toggleVideo = (video) => {
    setSelectedVideos((prev) => {
      // Single-select in picker mode; multi-select in library-save mode.
      if (pickerMode) {
        return prev[video.id] ? {} : { [video.id]: video };
      }
      const next = { ...prev };
      if (next[video.id]) delete next[video.id];
      else next[video.id] = video;
      return next;
    });
  };

  const handleAdd = async () => {
    const videos = Object.values(selectedVideos);
    if (!videos.length || isAdding) return;

    // Picker mode short-circuits the library save and hands the chosen video
    // back to the parent component.
    if (pickerMode) {
      const v = videos[0];
      onPickedVideo({
        id:        v.id,
        url:       `https://www.youtube.com/watch?v=${v.id}`,
        title:     v.title || `YouTube ${v.id}`,
        thumbnail: v.thumbnail,
        duration:  v.duration,
      });
      onHide?.();
      return;
    }

    setIsAdding(true);
    let added = 0;
    const createdMedia = [];
    try {
      for (const v of videos) {
        try {
          const res = await createLink({
            name: (v.title || `YouTube ${v.id}`).slice(0, 120),
            path: `https://www.youtube.com/watch?v=${v.id}`,
            type: 'youtube',
          });
          // /media/create returns the new id as `media_file` (JsonResource-wrapped
          // as { data: { status, msg, media_file } }). Grab it for auto-select.
          const body = res?.data ?? res;
          const id = body?.media_file ?? body?.id;
          if (id) createdMedia.push({ id });
          added += 1;
        } catch {
          // Continue with remaining; aggregate error surfaces below.
        }
      }
      if (added > 0) {
        dispatch(galleryRefetch());
        showToast('success', 'YouTube', t('links_.yt_added_count', { count: added }));
        // Let a caller (e.g. the café wizard) select the freshly-saved tracks.
        onAdded?.(createdMedia);
      }
      if (added < videos.length) {
        showToast('warn', 'YouTube', t('links_.yt_failed_count', { count: videos.length - added }));
      }
      onHide?.();
    } finally {
      setIsAdding(false);
    }
  };

  const selectedCount = Object.keys(selectedVideos).length;
  const hasCommitted = committed.length > 0;
  const visible_ = results.slice(0, visibleCount);
  const canLoadMore = visibleCount < results.length;

  return (
    <Dialog
      header={<span><i className="fab fa-youtube me-2" style={{ color: '#FF0000' }} /> {t('links_.yt_dialog_title')}</span>}
      visible={visible}
      onHide={onHide}
      style={{ width: '70vw', height: '80vh', maxHeight: '80vh' }}
      breakpoints={{ '1120px': '80vw', '960px': '90vw', '480px': '96vw' }}
      blockScroll
    >
      <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
        <div className="w-full my-3">
          <div className="d-flex gap-2">
            <div className="position-relative flex-1">
              <input
                type="text"
                className="form-control"
                style={{ height: 45 }}
                placeholder={t('links_.yt_search_placeholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runSearch();
                  }
                }}
                autoFocus
              />
              {query && !isSearching && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="btn btn-link p-0"
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                  title={t('buttons.clear')}
                >
                  ✕
                </button>
              )}
            </div>
            <Button
              icon="pi pi-search"
              label={t('buttons.search')}
              onClick={runSearch}
              disabled={!query.trim() || isSearching}
              loading={isSearching}
              className="btn btn-primary"
            />
          </div>
        </div>

        <hr className="w-100 mt-0" />

        <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
          {isSearching && (
            <div className="py-5 text-center w-100">
              <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="3" />
            </div>
          )}
          {!isSearching && !hasCommitted && (
            <h5 className="py-5 text-center text-muted w-100">
              {t('links_.yt_prompt')}
            </h5>
          )}
          {!isSearching && searchError && (
            <h5 className="py-5 text-center text-danger w-100">{searchError}</h5>
          )}
          {!isSearching && hasCommitted && !searchError && results.length === 0 && (
            <h5 className="py-5 text-center w-100">{t('messages.no_results')}</h5>
          )}
          {!isSearching && results.length > 0 && (
            <>
              <div className="d-grid gap-3 mb-3" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                {visible_.map((video) => {
                  const isSelected = !!selectedVideos[video.id];
                  const dur = formatDuration(video.duration);
                  return (
                    <div
                      key={video.id}
                      onClick={() => toggleVideo(video)}
                      className={`rounded border p-1 ${isSelected ? 'pp-selected-border' : 'border-secondary-subtle'}`}
                      style={{ cursor: 'pointer' }}
                    >
                      <div style={{ position: 'relative' }}>
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-100 rounded mb-1"
                          style={{ aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                          loading="lazy"
                          onError={(e) => { e.target.src = `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`; }}
                        />
                        {dur && (
                          <span
                            style={{
                              position: 'absolute',
                              right: 4,
                              bottom: 8,
                              background: 'rgba(0,0,0,0.8)',
                              color: '#fff',
                              padding: '1px 5px',
                              borderRadius: 3,
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {dur}
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-start gap-1 mb-1">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleVideo(video)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <p
                          className="m-0 fw-medium"
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: 12 }}
                          title={video.title}
                        >
                          {video.title}
                        </p>
                      </div>
                      {video.channel && (
                        <p className="m-0 text-muted" style={{ fontSize: 11 }}>{video.channel}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {t('links_.yt_showing', { shown: visible_.length, total: results.length })}
                </span>
                {canLoadMore && (
                  <Button
                    icon="pi pi-chevron-down"
                    label={t('links_.yt_load_more', { count: Math.min(PAGE_SIZE, results.length - visibleCount) })}
                    onClick={() => setVisibleCount((n) => Math.min(n + PAGE_SIZE, results.length))}
                    className="btn btn-outline-primary btn-sm"
                  />
                )}
              </div>
            </>
          )}
        </div>

        {selectedCount > 0 && (
          <div className="px-3 py-2 bg-white border-top" style={{ position: 'sticky', bottom: 0 }}>
            <Button
              icon={pickerMode ? 'pi pi-check' : 'pi pi-plus'}
              label={pickerMode
                ? t('links_.yt_use_video')
                : t('links_.yt_add_to_links', { count: selectedCount })}
              loading={isAdding}
              className="btn btn-primary w-100 text-center fs-6 p-2"
              onClick={handleAdd}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
